import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Download, Smartphone, Monitor, WifiOff } from 'lucide-react';
import DismissButton from '@/components/ui/DismissButton';
import {
  ACCENT, ACCENT_BRIGHT, FONT_DISPLAY, FONT_BODY, FONT_MONO,
  TEXT, HAIRLINE, glow, microLabel, primaryButton, ghostButton,
} from '@/styles/premium';

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
  prompt(): Promise<void>;
}

const PWAInstallPrompt: React.FC = () => {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Check if app is already installed (running in standalone mode)
    const checkStandalone = () => {
      return window.matchMedia('(display-mode: standalone)').matches || 
             (window.navigator as any).standalone === true;
    };

    setIsStandalone(checkStandalone());

    // Listen for the beforeinstallprompt event
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      
      // Show our custom install prompt after a delay
      setTimeout(() => {
        if (!checkStandalone()) {
          setShowInstallPrompt(true);
        }
      }, 3000); // Show after 3 seconds
    };

    // Listen for app installed event
    const handleAppInstalled = () => {
      setShowInstallPrompt(false);
      setDeferredPrompt(null);
      console.log('PWA was installed');
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;

    try {
      await deferredPrompt.prompt();
      const choiceResult = await deferredPrompt.userChoice;
      
      if (choiceResult.outcome === 'accepted') {
        console.log('User accepted the install prompt');
      } else {
        console.log('User dismissed the install prompt');
      }
      
      setDeferredPrompt(null);
      setShowInstallPrompt(false);
    } catch (error) {
      console.error('Error showing install prompt:', error);
    }
  };

  const handleDismiss = () => {
    setShowInstallPrompt(false);
    // Don't show again for this session
    sessionStorage.setItem('pwa-install-dismissed', 'true');
  };

  // Don't show if app is already installed or user dismissed it
  if (isStandalone || sessionStorage.getItem('pwa-install-dismissed')) {
    return null;
  }

  return (
    <AnimatePresence>
      {showInstallPrompt && (
        <motion.div
          initial={{ opacity: 0, y: 100 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 100 }}
          transition={{ duration: 0.3, ease: "easeOut" }}
          className="fixed left-3 right-3 sm:left-4 sm:right-4 md:left-auto md:right-4 md:max-w-sm"
          style={{
            zIndex: 50,
            bottom: 'calc(1rem + env(safe-area-inset-bottom))',
          }}
        >
          <div
            style={{
              background: 'linear-gradient(135deg,rgba(16,16,16,.97) 0%,rgba(10,10,10,.97) 100%)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(20,184,166,.18)',
              borderRadius: 16,
              padding: 'clamp(16px,4vw,20px)',
              fontFamily: FONT_BODY,
              color: TEXT.primary,
              boxShadow: '0 18px 50px rgba(0,0,0,.55)',
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            <div style={{ ...glow(180, 0.14), top: -70, right: -50 }} />

            <div style={{ position: 'relative', zIndex: 1 }}>
              {/* Header */}
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 14 }}>
                <div
                  style={{
                    width: 38, height: 38, borderRadius: 11, flexShrink: 0,
                    background: 'rgba(20,184,166,.15)',
                    border: '1px solid rgba(20,184,166,.25)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}
                >
                  <Download size={17} color={ACCENT} aria-hidden />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ ...microLabel, marginBottom: 4 }}>Install</div>
                  <div style={{ fontFamily: FONT_DISPLAY, fontSize: 19, fontWeight: 400, letterSpacing: '-.01em', lineHeight: 1.1 }}>
                    Add UniSchedule
                  </div>
                </div>
                <DismissButton onClick={handleDismiss} />
              </div>

              <p style={{ fontSize: 12.5, lineHeight: 1.65, color: TEXT.muted, fontWeight: 300, marginBottom: 14 }}>
                Keep your timetable one tap away — installs to your home screen and works offline.
              </p>

              {/* Capability row */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, marginBottom: 16 }}>
                {[
                  { Icon: Smartphone, label: 'Mobile' },
                  { Icon: Monitor, label: 'Desktop' },
                  { Icon: WifiOff, label: 'Offline' },
                ].map(({ Icon, label }) => (
                  <div
                    key={label}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 5,
                      fontFamily: FONT_MONO, fontSize: 9, letterSpacing: '.1em',
                      color: TEXT.faint, textTransform: 'uppercase',
                    }}
                  >
                    <Icon size={11} color="rgba(20,184,166,.6)" aria-hidden /> {label}
                  </div>
                ))}
              </div>

              {/* Actions */}
              <div style={{ display: 'flex', gap: 8, borderTop: `1px solid ${HAIRLINE}`, paddingTop: 16 }}>
                <ActionBtn onClick={handleDismiss} variant="ghost">Not now</ActionBtn>
                <ActionBtn onClick={handleInstallClick} variant="primary">Install</ActionBtn>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default PWAInstallPrompt;

// ─── Sub-components ───────────────────────────────────────────────────────────

function ActionBtn({
  onClick, variant, children,
}: { onClick: () => void; variant: 'primary' | 'ghost'; children: React.ReactNode }) {
  const [hov, setHov] = useState(false);
  const base = variant === 'primary' ? primaryButton : ghostButton;
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        ...base,
        flex: 1,
        padding: '11px 14px',
        ...(variant === 'primary'
          ? { background: hov ? ACCENT_BRIGHT : ACCENT, borderColor: hov ? ACCENT_BRIGHT : ACCENT }
          : {
              background: hov ? 'rgba(255,255,255,.05)' : 'transparent',
              borderColor: hov ? 'rgba(255,255,255,.2)' : 'rgba(255,255,255,.12)',
              color: hov ? 'rgba(255,255,255,.8)' : 'rgba(255,255,255,.6)',
            }),
      }}
    >
      {children}
    </button>
  );
}